'use client';

import React, { useState } from 'react';

import { TextPassage } from '@petsmart-ui/sparky';
import { ResponsivePicture, type PictureSize } from '@libs/shared-ui';
import {
    hoveredmageGalleryInstructions,
    imageGalleryInstructions,
} from '@libs/common-next';
import type { CarouselImageProps, CarouselImageSize } from './types';
import './CarouselImage.scss';

const componentClassName = 'carousel-image';

export function CarouselImage({
    mainSrc,
    imgAlt,
    priority,
    instructionText = imageGalleryInstructions,
    hoveredImageinstructionText = hoveredmageGalleryInstructions,
    enableMagnifier,
    onHover,
    onHoverLeave,
}: CarouselImageProps) {
    const IMAGE_SIZES: PictureSize[] = [
        { width: 400, height: 400 },
        { width: 800, height: 800 },
        { width: 1400, height: 1400 },
    ];
    const [containerSize, setContainerSize] = useState<CarouselImageSize>({
        width: 0,
        height: 0,
    });
    const [[x, y], setXY] = useState([0, 0]);
    const [showMagnifier, setShowMagnifier] = useState(false);

    const hoverMove = (e: React.MouseEvent<HTMLImageElement>) => {
        const elem = e.currentTarget;
        const { top, left } = elem.getBoundingClientRect();
        const posX = e.clientX - left;
        const posY = e.clientY - top;
        setXY([posX, posY]);
        if (onHover) {
            onHover({ imgUrl: mainSrc, imagePos: [posX, posY] });
        }
    };

    const hoverStart = (e: React.MouseEvent<HTMLImageElement>) => {
        setShowMagnifier(true);
        const elem = e.currentTarget;
        const { width, height } = elem.getBoundingClientRect();
        setContainerSize({ width, height });
    };

    const hoverEnd = () => {
        setShowMagnifier(false);
        if (onHoverLeave) {
            onHoverLeave();
        }
    };

    // Size of the pointer box
    const pointerBoxWidth = containerSize.width / 2;
    const pointerBoxHeight = containerSize.height / 2;

    // Position of pointer box
    const pointerBoxLeft = x - pointerBoxWidth / 2;
    const pointerBoxTop = y - pointerBoxHeight / 2;

    return (
        <div className={componentClassName}>
            <ResponsivePicture
                src={mainSrc}
                alt={imgAlt}
                sizes={IMAGE_SIZES}
                priority={priority}
                onMouseEnter={enableMagnifier ? hoverStart : undefined}
                onMouseMove={enableMagnifier ? hoverMove : undefined}
                onMouseLeave={enableMagnifier ? hoverEnd : undefined}
            />
            {enableMagnifier ? (
                <TextPassage
                    align="center"
                    className={`${componentClassName}__instructions`}
                >
                    {showMagnifier
                        ? hoveredImageinstructionText
                        : instructionText}
                </TextPassage>
            ) : null}
            {enableMagnifier ? (
                <div
                    className={`${componentClassName}__pointer--box`}
                    data-testid="carousel-image-pointer-box"
                    style={{
                        display: showMagnifier ? '' : 'none',
                        width: pointerBoxWidth,
                        height: pointerBoxHeight,
                        top: pointerBoxTop,
                        left: pointerBoxLeft,
                    }}
                />
            ) : null}
        </div>
    );
}
